import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok, OkVal } from '@core/model/framework/Result/OkResult';
import { PromiseTimedOut, promiseWithTimeout } from '@core/model/framework/promiseWithTimeout';
import { CaseClerkDto } from '@core/service/digital-factory/account/types/caseClerkDto';
import { BankConnectionLinkedNotification } from '@core/service/digital-factory/bank/types/signalRNotifications';
import { environment } from '@environments/environment';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { injectable } from 'inversify';

type TopicDict = {
  OPEN_BANKING_CASE_CLERK_STATUS_UPDATED: CaseClerkDto;
  BANK_CONNECTION_LINKED: BankConnectionLinkedNotification;
};

export type TopicName = keyof TopicDict;

type TopicType<T extends TopicName> = TopicDict[T];

@injectable()
export class SignalR {
  private signalRConnection!: HubConnection;
  private isInitialized: boolean;

  constructor() {
    this.isInitialized = false;
  }

  private async initialize(userToken: string): Promise<void> {
    if (this.isInitialized) {
      throw new Error('SignalR is already initialized');
    }

    this.signalRConnection = new HubConnectionBuilder()
      .withUrl(environment.urlSignalR, {
        headers: { Authorization: `Bearer ${userToken}` },
      })
      .build();

    this.signalRConnection.onclose = () => this.onClose();
    await this.signalRConnection.start();
    console.log('SignalR - initialized');
  }

  async ensureInitialization(userToken: string): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    return await this.initialize(userToken);
  }

  async waitOnSingleNotification<T extends TopicName>(
    topicName: T,
    timeoutInSeconds: number
  ): Promise<OkVal<TopicType<T>> | Bad<'NOTIFICATION_TIMED_OUT'>> {
    try {
      const signalRNotification = new Promise<TopicType<T>>((resolve) => {
        console.log(`SignalR - waitOnSingleNotification, topicName: ${topicName}`);
        this.signalRConnection.on(topicName, (message: TopicType<T>) => {
          resolve(message);
        });
      });

      const result = await promiseWithTimeout(signalRNotification, timeoutInSeconds * 1000);

      console.log('SignalR - waitOnSingleNotification - Notification received on topic : ', {
        topicName,
        result,
      });

      return Ok(result as TopicType<T>);
    } catch (e) {
      console.log('SignalR - waitOnSingleNotification - Error on topic : ', {
        topicName,
        e,
      });
      if (e instanceof PromiseTimedOut) {
        return Bad('NOTIFICATION_TIMED_OUT');
      }
      throw e;
    } finally {
      this.disposeHandler(topicName);
    }
  }

  disposeHandler(topicName: TopicName): void {
    console.log('SignalR - disposeHandler - ', topicName);
    this.signalRConnection.off(topicName);
  }

  private onClose(): void {
    this.signalRConnection.onclose((err) => {
      console.error(err);
    });
  }
}
